import { prepareHtmlContent } from './htmlUtils';


const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    } catch {
        return dateString;
    }
};


const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
        if (timeString.includes('T')) {
            const date = new Date(timeString);
            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
        }
        if (timeString.includes(':')) {
            const parts = timeString.split(':');
            let hours = parseInt(parts[0]);
            const minutes = parts[1].padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
        }
        return timeString;
    } catch {
        return timeString;
    }
};


// Load images as base64
const loadImageAsBase64 = async (url) => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('Error loading image:', error);
        return null;
    }
};


// Create header element
const createHeaderElement = (headerImgBase64, userInfo, reportColor) => {
    const header = document.createElement('div');
    header.className = 'pdf-page-header';
    header.innerHTML = `
        <img src="${headerImgBase64}" alt="Header" style="width: 100%; display: block;" />
        <div style="
            width: 100%;
            padding: 10px 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #f8f8f8;
            font-size: 12px;
            box-sizing: border-box;
            border-bottom: 2px solid ${reportColor};
        ">
            <div style="display: flex; flex-direction: column; gap: 4px;">
                <div style="display: flex; gap: 8px;">
                    <span style="font-weight: 700; color: ${reportColor};">Name:</span>
                    <span style="font-weight: 500; color: #333;">${userInfo.name}</span>
                </div>
                <div style="display: flex; gap: 8px;">
                    <span style="font-weight: 700; color: ${reportColor};">Email:</span>
                    <span style="font-weight: 500; color: #333;">${userInfo.email}</span>
                </div>
            </div>
            <div style="text-align: right;">
                <div style="font-weight: 700; color: ${reportColor}; font-size: 11px;">Generated On</div>
                <div style="font-weight: 500; color: #333; font-size: 11px;">${userInfo.date}</div>
            </div>
        </div>
    `;
    return header;
};


// Create footer element
const createFooterElement = (footerImgBase64, reportColor) => {
    const footer = document.createElement('div');
    footer.className = 'pdf-page-footer';
    footer.style.borderTop = `2px solid ${reportColor}`;
    footer.innerHTML = `<img src="${footerImgBase64}" alt="Footer" style="width: 100%; display: block;" />`;
    return footer;
};


// Split content into manageable chunks for better page breaks
const splitContentIntoSections = (htmlContent) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    const sections = [];
    const children = Array.from(tempDiv.children);
    
    children.forEach(child => {
        sections.push(child.outerHTML);
    });
    
    return sections;
};


export const generatePDF = async ({
    reportContent,
    selectedReportType,
    reportData,
    userName,
    userProfile,
    authUser,
    onProgress
}) => {
    try {
        console.log('📄 Starting PDF generation with smart pagination...');


        // Import dependencies
        const { default: jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;


        const cleanContent = prepareHtmlContent(reportContent);
        const reportColor = selectedReportType.color || '#4A90E2';


        // Read from environment variables
        const headerImgUrl = process.env.REACT_APP_HEADER_IMG_URL || 'https://res.cloudinary.com/ddgd5aq1u/image/upload/v1764766399/Heading3_gdltit.png';
        const footerImgUrl = process.env.REACT_APP_FOOTER_IMG_URL || 'https://res.cloudinary.com/ddgd5aq1u/image/upload/v1764671495/Footer_po0kk1.png';


        // Get user info
        const displayName = authUser?.fullname || authUser?.name || userName || 'Guest User';
        const displayEmail = authUser?.email || userProfile?.email || 'Email not available';
        const generatedDateTime = (() => {
            const now = new Date();
            return `${formatDate(now.toISOString())} ${formatTime(now.toISOString())}`;
        })();


        const userInfo = {
            name: displayName,
            email: displayEmail,
            date: generatedDateTime
        };


        if (onProgress) onProgress(10);


        // Load images as base64
        console.log('Loading header and footer images...');
        const [headerImgBase64, footerImgBase64] = await Promise.all([
            loadImageAsBase64(headerImgUrl),
            loadImageAsBase64(footerImgUrl)
        ]);


        if (onProgress) onProgress(20);


        // Create header and footer canvases first to get their heights
        const headerElement = createHeaderElement(headerImgBase64, userInfo, reportColor);
        const footerElement = createFooterElement(footerImgBase64, reportColor);
        
        headerElement.style.cssText = 'width: 210mm; position: absolute; left: -9999px; top: 0;';
        footerElement.style.cssText = 'width: 210mm; position: absolute; left: -9999px; top: 0;';
        document.body.appendChild(headerElement);
        document.body.appendChild(footerElement);


        const headerCanvas = await html2canvas(headerElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false
        });


        const footerCanvas = await html2canvas(footerElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false
        });


        document.body.removeChild(headerElement);
        document.body.removeChild(footerElement);


        if (onProgress) onProgress(30);


        // A4 dimensions
        const pageWidth = 210;
        const pageHeight = 297;
        const headerHeight = (headerCanvas.height * pageWidth) / headerCanvas.width;
        const footerHeight = (footerCanvas.height * pageWidth) / footerCanvas.width;
        const contentAreaHeight = pageHeight - headerHeight - footerHeight - 15; // 15mm total margins


        // Initialize PDF
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true
        });


        const headerImg = headerCanvas.toDataURL('image/jpeg', 0.98);
        const footerImg = footerCanvas.toDataURL('image/jpeg', 0.98);


        // Split content into sections for better control
        const contentSections = splitContentIntoSections(cleanContent);
        
        if (onProgress) onProgress(40);


        // Process content section by section
        let currentPageY = 0;
        let pageNumber = 0;
        let totalPagesCreated = 1; // Track total pages to add footers later
        const contentX = 20; // 20mm left margin
        const contentWidth = pageWidth - 40; // 20mm margins on both sides
        const yOffset = headerHeight + 8; // Start position after header
        const footerY = pageHeight - footerHeight; // Fixed footer position


        for (let i = 0; i < contentSections.length; i++) {
            // Create temporary container for this section
            const sectionContainer = document.createElement('div');
            sectionContainer.style.cssText = `
                width: ${contentWidth}mm;
                background: white;
                padding: 0;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                color: #333;
                position: absolute;
                left: -9999px;
                top: 0;
            `;
            
            sectionContainer.innerHTML = `
                <style>
                    .pdf-section-content h1 {
                        font-size: 28px;
                        color: ${reportColor};
                        margin: 0 0 15px 0;
                        font-weight: 700;
                        line-height: 1.4;
                    }
                    .pdf-section-content h2 {
                        font-size: 20px;
                        color: ${reportColor};
                        margin: 20px 0 12px 0;
                        font-weight: 600;
                        border-bottom: 2px solid ${reportColor};
                        padding-bottom: 8px;
                    }
                    .pdf-section-content h3 {
                        font-size: 15px;
                        color: #333;
                        margin: 12px 0 8px 0;
                        font-weight: 600;
                    }
                    .pdf-section-content p {
                        font-size: 13px;
                        color: #555;
                        line-height: 1.7;
                        margin: 0 0 10px 0;
                        text-align: justify;
                    }
                    .pdf-section-content ul, .pdf-section-content ol {
                        margin: 0 0 12px 20px;
                        font-size: 13px;
                        line-height: 1.7;
                        padding: 0;
                    }
                    .pdf-section-content li {
                        margin-bottom: 5px;
                        color: #555;
                    }
                    .pdf-section-content strong {
                        color: ${reportColor};
                        font-weight: 600;
                    }
                    .pdf-section-content table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                    }
                    .pdf-section-content table th {
                        background: ${reportColor};
                        color: white;
                        padding: 10px;
                        text-align: left;
                        font-weight: 600;
                        font-size: 12px;
                        border: 1px solid ${reportColor};
                    }
                    .pdf-section-content table td {
                        padding: 8px 10px;
                        border: 1px solid #ddd;
                        font-size: 12px;
                        color: #555;
                    }
                    .pdf-section-content table tr:nth-child(even) {
                        background: #f9f9f9;
                    }
                    .pdf-section-content hr {
                        border: none;
                        border-top: 2px solid ${reportColor};
                        margin: 20px 0;
                        opacity: 0.5;
                    }
                </style>
                <div class="pdf-section-content">${contentSections[i]}</div>
            `;
            
            document.body.appendChild(sectionContainer);


            // Render section to canvas
            const sectionCanvas = await html2canvas(sectionContainer, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false
            });


            document.body.removeChild(sectionContainer);


            const sectionHeightPx = sectionCanvas.height;
            const sectionHeightMm = (sectionHeightPx * contentWidth) / sectionCanvas.width;


            // Check if section fits on current page
            if (currentPageY + sectionHeightMm > contentAreaHeight) {
                // Section doesn't fit, need to split or move to next page
                
                if (sectionHeightMm <= contentAreaHeight) {
                    // Section fits on a single page, move to next page
                    pdf.addPage();
                    pageNumber++;
                    totalPagesCreated++;
                    
                    // Add header
                    pdf.addImage(headerImg, 'JPEG', 0, 0, pageWidth, headerHeight);
                    
                    // Add section
                    const sectionImg = sectionCanvas.toDataURL('image/jpeg', 0.98);
                    pdf.addImage(sectionImg, 'JPEG', contentX, yOffset, contentWidth, sectionHeightMm);
                    
                    currentPageY = sectionHeightMm;
                } else {
                    // Section is larger than a page, need to split it
                    let remainingSectionHeight = sectionHeightPx;
                    let sectionOffsetY = 0;


                    while (remainingSectionHeight > 0) {
                        if (pageNumber > 0 || currentPageY > 0) {
                            pdf.addPage();
                            pageNumber++;
                            totalPagesCreated++;
                            currentPageY = 0;
                        }


                        // Add header
                        if (pageNumber === 0 && currentPageY === 0) {
                            // First page, header already added
                        } else {
                            pdf.addImage(headerImg, 'JPEG', 0, 0, pageWidth, headerHeight);
                        }


                        // Calculate available space
                        const availableHeightMm = contentAreaHeight - currentPageY;
                        const availableHeightPx = (availableHeightMm * sectionCanvas.width) / contentWidth;
                        const heightToRenderPx = Math.min(availableHeightPx, remainingSectionHeight);


                        // Create slice
                        const sliceCanvas = document.createElement('canvas');
                        sliceCanvas.width = sectionCanvas.width;
                        sliceCanvas.height = heightToRenderPx;
                        const sliceCtx = sliceCanvas.getContext('2d');
                        
                        sliceCtx.drawImage(
                            sectionCanvas,
                            0, sectionOffsetY,
                            sectionCanvas.width, heightToRenderPx,
                            0, 0,
                            sectionCanvas.width, heightToRenderPx
                        );


                        const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.98);
                        const sliceHeightMm = (heightToRenderPx * contentWidth) / sectionCanvas.width;
                        
                        pdf.addImage(sliceImg, 'JPEG', contentX, yOffset + currentPageY, contentWidth, sliceHeightMm);


                        sectionOffsetY += heightToRenderPx;
                        remainingSectionHeight -= heightToRenderPx;
                        currentPageY = sliceHeightMm;
                    }
                }
            } else {
                // Section fits on current page
                if (pageNumber === 0 && currentPageY === 0) {
                    // First page, add header first
                    pdf.addImage(headerImg, 'JPEG', 0, 0, pageWidth, headerHeight);
                }


                const sectionImg = sectionCanvas.toDataURL('image/jpeg', 0.98);
                pdf.addImage(sectionImg, 'JPEG', contentX, yOffset + currentPageY, contentWidth, sectionHeightMm);
                currentPageY += sectionHeightMm;
            }


            if (onProgress) {
                const progress = 40 + ((i + 1) / contentSections.length) * 50;
                onProgress(Math.min(90, progress));
            }
        }


        if (onProgress) onProgress(92);


        // **NEW: Add footer to ALL pages by iterating through each page**
        console.log(`Adding footers to ${totalPagesCreated} pages...`);
        for (let p = 1; p <= totalPagesCreated; p++) {
            pdf.setPage(p);
            pdf.addImage(footerImg, 'JPEG', 0, footerY, pageWidth, footerHeight);
        }


        if (onProgress) onProgress(95);


        // Save PDF
        const filename = `${selectedReportType.name || 'Report'}_${formatDate(new Date().toISOString())}.pdf`;
        pdf.save(filename);


        if (onProgress) onProgress(100);


        console.log('✅ PDF generated successfully!');
        return { success: true, message: 'PDF downloaded successfully!' };


    } catch (error) {
        console.error(' Error generating PDF:', error);
        return { success: false, error: error.message || 'Failed to generate PDF' };
    }
};


export default generatePDF;
